import React from "react";
import { useLocation, useNavigate,  } from "react-router-dom";
import "./ViewPage.scss";
import logo from "images/edubriczlogooriginal.png";
import { getRequest } from "Includes/api/apicall";
import { GET_URL } from "Includes/urls";
import {
  normalizeServices,
  erpAmountBeforeGst,
  erpTotalInclGst,
  idCardAmountTotal,
  servicesTotalInclGst,
  digitalPricesTotal,
  singleDigitalFeatureAmount,
  grandTotalAgreement,
} from "Containers/Agreement/agreementFinance";

const LOCAL_STORAGE_FORM_KEY = "agreementFormData";

/** Safe Windows/macOS filename fragment from institute name */
const sanitizeFileBase = (name) => {
  const raw = String(name || "Agreement")
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "")
    .trim();
  const single = raw.replace(/\s+/g, "_");
  return single.slice(0, 120) || "Agreement";
};

const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Minimal styles for print iframe (avoids browser printing main page URL in header/footer). */
const PREVIEW_IFRAME_STYLES = `
  body { margin: 0; padding: 0; font-family: Georgia, 'Times New Roman', serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .pdfContent { width: 100%; max-width: 794px; margin: 0 auto; padding: 20px 48px; background: #fff; box-sizing: border-box; }
  .pdfHeader { width: 100%; text-align: center; margin: 0 auto; }
  .pdfLogo { width: 180px;height: 180px; max-width: 100%; object-fit: contain; display: block; margin: 0 auto 0; float: none; }
  .pdfTitle { font-size: 18px; font-weight: bold; text-align: center;margin-top: 0px; }
  .pdfSubtitle { font-size: 10pt; width: 100%; max-width: 420px; margin: 0 auto 16px; text-align: center; }
  .pdfText { font-size: 12pt; line-height: 2; text-align: left; }
  .pdfTable { width: 100%; border-collapse: collapse; margin-top: 16px; }
  .pdfTable th, .pdfTable td { border: 2px solid #000; padding: 10px; text-align: center; }
  .planTable { margin-top: 10px; width: 100%; border-collapse: collapse; text-align: left; }
  .planTable td, .planTable th { border: 2px solid #000; padding: 3%; width: 50%; vertical-align: top; }
  .planTable td ul li { margin-left: 10px; }
  .planTable td ul li ul li { margin-left: 20px; }
  .terms li { margin-top: 10px; line-height: 1.6; }
  .summary { margin-top: 16px; }
  .summary p { margin-left: 12px; margin-top: 8px; }
  .billingSummaryTable { width: 100%; border-collapse: collapse; margin-top: 8px; margin-bottom: 12px; font-size: 11pt; }
  .billingSummaryTable th, .billingSummaryTable td { border: 1px solid #000; padding: 8px 10px; text-align: left; vertical-align: top; }
  .billingSummaryTable thead th { background: #f0f0f0; font-weight: bold; }
  .billingSummaryTable .amountCol { text-align: right; white-space: nowrap; }
  .billingSummaryTable tr.billingSection td { background: #f5f5f5; font-weight: bold; }
  .billingSummaryTable tr.billingSubtotal td { font-weight: bold; }
  .billingSummaryTable .calcCell { line-height: 1.55; }
  .scheduleTable { width: 100%; border-collapse: collapse; margin-top: 10px; margin-bottom: 8px; }
  .scheduleTable th, .scheduleTable td { border: 1px solid #000; padding: 8px; font-size: 11pt; }
  .scheduleTable th { background: #f0f0f0; text-align: left; }
  .scheduleTable td:nth-child(3), .scheduleTable td:nth-child(4) { text-align: right; }
  .services div { margin-top: 16px; }
  .signatureTable { margin-top: 48px; border-collapse: collapse; width: 100%; }
  .signatureTable td, .signatureTable th { border: 2px solid #000; padding: 3%; width: 50%; }
  .pdfTable, .planTable, .scheduleTable, .signatureTable { page-break-inside: auto; break-inside: auto; }
  .pdfTable tr, .planTable tr, .scheduleTable tr, .signatureTable tr { page-break-inside: avoid; break-inside: avoid; }
  .pdfTable td, .pdfTable th, .planTable td, .planTable th, .scheduleTable td, .scheduleTable th, .signatureTable td, .signatureTable th { page-break-inside: avoid; break-inside: avoid; }
  @media print {
    body { margin: 0; }
    @page { size: A4; margin: 16mm 16mm 14mm 16mm; }
    .pdfContent { padding: 0; }
    .pdfHeader { text-align: center !important; }
    .pdfLogo { margin-left: auto !important; margin-right: auto !important; float: none !important; }
  }
`;

const normalizeData = (raw) => {
  const data = raw || {};
  const digitalfeature =
    data.digitalfeature && typeof data.digitalfeature === "object" && !Array.isArray(data.digitalfeature)
      ? data.digitalfeature
      : {};
  return {
    ...data,
    pricing: Array.isArray(data.pricing) ? data.pricing : [],
    subscription: Array.isArray(data.subscription) ? data.subscription : [],
    addOns: Array.isArray(data.addOns) ? data.addOns : [],
    planNotes: Array.isArray(data.planNotes) ? data.planNotes : [],
    paymentTerms: Array.isArray(data.paymentTerms) ? data.paymentTerms : [],
    erpfeature: Array.isArray(data.erpfeature) ? data.erpfeature : [],
    digitalfeature,
    services: normalizeServices(data.services),
  };
};

const PreviewPage = () => {
  const location = useLocation();
  const history = useNavigate();
  const pdfRef = React.useRef(null);
  const [erpCatalog, setErpCatalog] = React.useState([]);
  const [digitalCatalog, setDigitalCatalog] = React.useState([]);

  const data = React.useMemo(() => {
    const fromRoute = location.state?.data;
    if (fromRoute) return normalizeData(fromRoute);
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_FORM_KEY);
      return normalizeData(saved ? JSON.parse(saved) : null);
    } catch {
      return normalizeData(null);
    }
  }, [location.state, location.key]);

  React.useEffect(() => {
    getRequest(GET_URL.erpfeature.api, { limit: 500, pageno: 1 }, {})
      .then((res) => {
        if (res?.status === 200) setErpCatalog(res.data?.data || []);
      })
      .catch(() => {});
    getRequest(GET_URL.digitalmarketingfeature.api, { limit: 500, pageno: 1 }, {})
      .then((res) => {
        if (res?.status === 200) setDigitalCatalog(res.data?.data || []);
      })
      .catch(() => {});
  }, []);

  const erpIdToName = React.useMemo(() => {
    const m = {};
    (erpCatalog || []).forEach((f) => {
      m[String(f.id)] = f.name || `ID ${f.id}`;
    });
    return m;
  }, [erpCatalog]);

  const digitalById = React.useMemo(() => {
    const m = {};
    (digitalCatalog || []).forEach((f) => {
      m[String(f.id)] = f;
    });
    return m;
  }, [digitalCatalog]);

  const erpFeatureDisplay = (item) => {
    const s = String(item);
    if (/^\d+$/.test(s)) return erpIdToName[s] || `ERP #${s}`;
    return s;
  };

  const digitalRows = React.useMemo(() => {
    const raw = data.digitalfeature || {};
    return Object.entries(raw).map(([id, v]) => {
      let price = "";
      let renewal = "";
      if (Array.isArray(v)) {
        price = v[0];
        renewal = v[1];
      } else if (v && typeof v === "object") {
        price = v.price;
        renewal = v.renewal_price;
      }
      return { id: String(id), price, renewal };
    });
  }, [data.digitalfeature]);

  const digitalTermsBlocks = React.useMemo(() => {
    const blocks = [];
    digitalRows.forEach((row) => {
      const meta = digitalById[row.id];
      const name = meta?.name || `Other Service #${row.id}`;
      const terms = Array.isArray(meta?.terms_and_condition)
        ? meta.terms_and_condition.filter((t) => t != null && String(t).trim() !== "")
        : [];
      if (terms.length) blocks.push({ name, terms });
    });
    return blocks;
  }, [digitalRows, digitalById]);

  const generatePrint = () => {
    const node = pdfRef.current;
    if (!node) return;

    const printTitle = " ";

    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "print");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
    document.body.appendChild(iframe);

    const idoc = iframe.contentDocument;
    if (!idoc) {
      document.body.removeChild(iframe);
      return;
    }

    const clone = node.cloneNode(true);
    idoc.open();
    idoc.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${escapeHtml(printTitle)}</title>` +
        `<style>${PREVIEW_IFRAME_STYLES}</style></head><body></body></html>`
    );
    idoc.body.appendChild(clone);
    idoc.close();

    const cleanup = () => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    const doPrint = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } finally {
        setTimeout(cleanup, 500);
      }
    };

    setTimeout(doPrint, 200);
  };

  React.useEffect(() => {
    if (location.state?.autoPrint) {
      const timer = setTimeout(() => {
        generatePrint();
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.autoPrint, data.schoolName]);

  // Ensure browser print header title stays empty even when user uses Ctrl+P.
  React.useEffect(() => {
    const originalTitle = document.title;
    const beforePrint = () => {
      document.title = "";
    };
    const afterPrint = () => {
      document.title = "";
    };

    window.addEventListener("beforeprint", beforePrint);
    window.addEventListener("afterprint", afterPrint);

    return () => {
      window.removeEventListener("beforeprint", beforePrint);
      window.removeEventListener("afterprint", afterPrint);
      document.title = "";
    };
  }, []);

  if (!data?.schoolName) {
    return (
      <div className="agreementSubmitContainer">
        <div className="pdfContent">
          <h3>No agreement data found.</h3>
          <p>Please create a new agreement using the Order Form.</p>
          <div className="pdfButtonRow">
            <button className="pdfButton" onClick={() => history("/agreement/add")}>
              Go to Add Agreement
            </button>
          </div>
        </div>
      </div>
    );
  }

  const fmtDate = (iso) => {
    if (!iso) return "";
    const parts = String(iso).split("-");
    if (parts.length !== 3) return String(iso);
    return parts.reverse().join("-");
  };

  const erpTotal = erpTotalInclGst(data);
  const erpBase = erpAmountBeforeGst(data);
  const erpGstAmount = erpTotal - erpBase;
  const idCardTotal = idCardAmountTotal(data);
  const servicesTotal = servicesTotalInclGst(data.services);
  const digitalTotal = digitalPricesTotal(data.digitalfeature, data);
  const grandTotal = grandTotalAgreement(data);
  const fmtRs = (n) =>
    Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const hasServices = data.services && Object.keys(data.services).length > 0;
  const hasDigital = digitalRows.length > 0;
  const servicesMap = normalizeServices(data.services);
  const serviceEntries = Object.entries(servicesMap);

  const paymentScheduleTable =
    data.paymentTerms.length > 0 ? (
      <table className="scheduleTable">
        <thead>
          <tr>
            <th>Term</th>
            <th>Due date</th>
            <th>Share %</th>
            <th>Amount (Rs.)</th>
          </tr>
        </thead>
        <tbody>
          {data.paymentTerms.map((term, index) => {
            const pct = Number(term.payment || 0);
            const amt = (pct / 100) * grandTotal;
            const due = term.date
              ? new Date(term.date).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "—";
            return (
              <tr key={`pt-${index}`}>
                <td>{`Instalment ${index + 1}`}</td>
                <td>{due}</td>
                <td>{pct}%</td>
                <td>{amt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    ) : null;

  return (
    <div className="agreementSubmitContainer">
      <style>{`
        .agreementSubmitContainer {
          background: #f2f3f5;
          padding: 16px;
        }
        #pdfContent {
          width: 210mm !important;
          min-height: 297mm;
          margin: 0 auto !important;
          padding: 16mm 16mm 14mm 16mm !important;
          box-sizing: border-box !important;
          background: #fff;
          border: 1px solid #ddd;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        }
        #pdfContent .pdfHeader {
          width: 100%;
          text-align: center;
          margin: 0 auto;
        }
        #pdfContent .pdfLogo {
          display: block;
          margin-left: auto;
          margin-right: auto;
          float: none;
        }
        @media print {
          body * {
            visibility: hidden !important;
          }
          #pdfContent, #pdfContent * {
            visibility: visible !important;
          }
          #pdfContent {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border: none !important;
          }
          .pdfButtonRow {
            display: none !important;
          }
          @page {
            size: A4;
            margin: 16mm 16mm 14mm 16mm;
          }
          #pdfContent {
            padding: 0 !important;
            box-sizing: border-box !important;
          }
          #pdfContent .pdfHeader {
            width: 100% !important;
            text-align: center !important;
            margin: 0 auto !important;
          }
          #pdfContent .pdfLogo {
            display: block !important;
            margin-left: auto !important;
            margin-right: auto !important;
            float: none !important;
          }
          #pdfContent .pdfTable,
          #pdfContent .planTable,
          #pdfContent .scheduleTable,
          #pdfContent .signatureTable {
            page-break-inside: auto !important;
            break-inside: auto !important;
          }
          #pdfContent .pdfTable tr,
          #pdfContent .planTable tr,
          #pdfContent .scheduleTable tr,
          #pdfContent .signatureTable tr {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
      `}</style>
      <div ref={pdfRef} className="pdfContent" id="pdfContent">
        <div className="pdfHeader">
          <img src={logo} alt="Logo" className="pdfLogo" />
          <h2 className="pdfTitle">
            <span style={{ fontSize: 30 }}>E</span>dubricz <span style={{ fontSize: 30 }}>T</span>echnologies
          </h2>
          <p className="pdfSubtitle">#B/2, 3rd Floor, Industrial Town, West of Chord Road, Rajajinagar, Bangalore- 560010</p>
          <h3 style={{ marginBottom: 40 }}>Order Form</h3>
        </div>

        <p className="pdfText">
          The customer identified below (“Licensee”) will purchase from Edubricz Technologies LLP (“Company”), and the Company will provide
          Licensee the subscriptions listed in this Order Form.
        </p>

        <table className="pdfTable">
          <thead>
            <tr>
              <th></th>
              <th style={{ fontWeight: "bold" }}>License Details</th>
              <th style={{ fontWeight: "bold" }}>Company Details</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ fontWeight: "bold" }}>Legal Entity Name</td>
              <td>{data.schoolName}</td>
              <td>Edubricz Technologies LLP</td>
            </tr>
            <tr>
              <td style={{ fontWeight: "bold" }}>Registered Address</td>
              <td>{data.schoolAddress}</td>
              <td>#B/2 3rd Floor, West of Chord Road, Bengaluru</td>
            </tr>
            <tr>
              <td style={{ fontWeight: "bold" }}>Billing Contact Details Name</td>
              <td>{data.contactPerson}</td>
              <td>{data.salesPersonName}</td>
            </tr>
          </tbody>
        </table>

        <div className="pdfText" style={{ marginTop: 20 }}>
          <p style={{ fontWeight: "bold" }}>Subscription Details</p>
          {data.agreementdate && <p style={{ fontWeight: "bold" }}>Billing Effective Date: {fmtDate(data.agreementdate)}</p>}
          <p style={{ fontWeight: "bold" }}>Modules Subscribed: Edubricz Plan</p>
        </div>

        <table className="planTable">
          <thead>
            <tr>
              <th>Plan Type</th>
              <th>Edubricz Plan</th>
            </tr>
          </thead>
          <tbody>
            {data.peramount && (
              <tr>
                <td>Amount</td>
                <td>Rs. {data.peramount}</td>
              </tr>
            )}
            {data.perId && (
              <tr>
                <td>Amount per ID card</td>
                <td>Rs. {data.perId}</td>
              </tr>
            )}
            {Number(data.perId || 0) > 0 && Number(data.numberOfStudents || 0) > 0 && (
              <tr>
                <td>ID card total</td>
                <td>
                  Rs. {Number(data.perId || 0).toFixed(2)} x {Number(data.numberOfStudents || 0)} ={" "}
                  {idCardTotal.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
            )}
            <tr>
              <td>Plan Details</td>
              <td>
                <ul>
                  <li>Application setup and usage for all teachers and students of the Licensee.</li>
                  <li>
                    Admin controls and dashboards including:
                    <ul>
                      <li>Summary analytics</li>
                      <li>Add/remove classrooms, students and teachers</li>
                      <li>
                        <strong>ERP Features:</strong>
                        <ul>
                          {data.erpfeature.map((item, i) => (
                            <li key={`${item}-${i}`}>{erpFeatureDisplay(item)}</li>
                          ))}
                        </ul>
                      </li>
                      <li>
                        <strong>Additional Pricing:</strong>
                        <ul>
                          <li>Free sms of {data.fsmscost || 1000}</li>
                          <li>Free email of {data.femailcost || 1000}</li>
                          <li>Additional SMS Cost: ₹{data.smscost || 0.25} per SMS</li>
                          <li>Additional Email Cost: ₹{data.emailcost || 0.25} per email</li>
                          <li>IVR Service Cost: ₹{data.ivrcost || 0.4} per 30s pulse</li>
                          {data.pricing.map((p, idx) => (
                            <li key={`${p}-${idx}`}>{p}</li>
                          ))}
                        </ul>
                      </li>
                    </ul>
                  </li>
                </ul>
              </td>
            </tr>
            {(data.fromDate || data.toDate) && (
              <tr>
                <td>Duration</td>
                <td>
                  {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
                </td>
              </tr>
            )}
            <tr>
              <td>Official Website</td>
              <td>
                <a href="https://www.edubricz.com">www.edubricz.com</a>
              </td>
            </tr>
            {data.addOns.map((item, i) => {
              const [k, v] = String(item).split("||");
              return (
                <tr key={`${item}-${i}`}>
                  <td>{(k || "").trim()}</td>
                  <td>{(v || "").trim()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <h4 style={{ textAlign: "left", marginTop: 20 }}>Payment Terms:</h4>
        <ul style={{ textAlign: "left" }} className="terms">
          {data.increasePercentage && (
            <li>Amount will be revised for upcoming years based on Inflation ({data.increasePercentage}% of the discussed price).</li>
          )}
          {data.subscription.map((item, i) => (
            <li key={`${item}-${i}`}>{item}</li>
          ))}
        </ul>
        {paymentScheduleTable ? (
          <div style={{ marginTop: 16 }}>
            <p style={{ fontWeight: "bold", textAlign: "left", marginBottom: 8 }}>Instalment division</p>
            {paymentScheduleTable}
          </div>
        ) : null}

        <h4 style={{ marginTop: 20 }}>Billing Summary</h4>
        <div className="summary">
  <table className="billingSummaryTable">
    <thead>
      <tr>
        <th style={{ width: "8%" }}>#</th>
        <th style={{ width: "22%" }}>Service</th>
        <th style={{ width: "45%" }}>Calculation</th>
        <th className="amountCol" style={{ width: "25%" }}>
          Amount (Rs.)
        </th>
      </tr>
    </thead>

    <tbody>
      <tr className="billingSection">
        <td colSpan={4}>1. ERP </td>
      </tr>

      <tr>
        <td />
        <td>ERP </td>
        <td className="calcCell">
          {data.amountEntry === "Number of students" && (
            <>
              Rs. {Number(data.peramount || 0).toFixed(2)} ×{" "}
              {Number(data.numberOfStudents || 0)} students = Rs.{" "}
              {fmtRs(erpBase)} 
            </>
          )}

          {data.amountEntry === "Up to student" && (
            <>
              Flat licence (up to{" "}
              {Number(data.numberOfStudents || 0)} students): Rs.{" "}
              {Number(data.peramount || 0).toFixed(2)} → base Rs.{" "}
              {fmtRs(erpBase)} 
            </>
          )}

          {!data.amountEntry && (
            <>Base = Rs. {fmtRs(erpBase)}</>
          )}
        </td>

        <td className="amountCol">{fmtRs(erpBase)}</td>
      </tr>

      {/* <tr>
        <td />
        <td>GST ({Number(data.gst || 0)}%)</td>
        <td className="calcCell">On ERP base above</td>
        <td className="amountCol">{fmtRs(erpGstAmount)}</td>
      </tr> */}

      <tr className="billingSubtotal">
        <td />
        <td colSpan={2}>
          <b>Total ERP </b>
        </td>
        <td className="amountCol">
          <b>{fmtRs(erpTotal)}</b>
        </td>
      </tr>

      {/* SHOW ONLY IF SERVICES AVAILABLE */}
      {hasServices && (
        <>
          <tr className="billingSection">
            <td colSpan={4}>2. Add-on services (incl. GST)</td>
          </tr>

          {serviceEntries.map(([name, meta]) => {
            const amount = Number(meta?.Amount ?? 0);
            const gstPct = Number(meta?.GST ?? 0);
            const gstAmount = gstPct ? amount * (gstPct / 100) : 0;
            const lineTotal = amount + gstAmount;

            return (
              <tr key={name}>
                <td />
                <td>{name}</td>

                <td className="calcCell">
                  Base Rs. {amount.toFixed(2)}
                  {gstPct
                    ? ` + GST ${gstPct}% (Rs. ${fmtRs(gstAmount)})`
                    : ""}
                  {meta?.Description ? ` — ${meta.Description}` : ""}
                </td>

                <td className="amountCol">{fmtRs(lineTotal)}</td>
              </tr>
            );
          })}

          <tr className="billingSubtotal">
            <td />
            <td colSpan={2}>
              <b>Subtotal (add-on services)</b>
            </td>

            <td className="amountCol">
              <b>{fmtRs(servicesTotal)}</b>
            </td>
          </tr>
        </>
      )}

      {/* SHOW ONLY IF PER ID EXISTS */}
      {Number(data.perId || 0) > 0 && (
        <>
          <tr className="billingSection">
            <td colSpan={4}>3. ID cards</td>
          </tr>

          <tr>
            <td />
            <td>ID cards</td>

            <td className="calcCell">
              Rs. {Number(data.perId || 0).toFixed(2)} ×{" "}
              {Number(data.numberOfStudents || 0)} student(s)
            </td>

            <td className="amountCol">{fmtRs(idCardTotal)}</td>
          </tr>
        </>
      )}

      {/* SHOW ONLY IF DIGITAL FEATURES AVAILABLE */}
      {hasDigital && (
        <>
          <tr className="billingSection">
            <td colSpan={4}>4. Other services </td>
          </tr>

          {Object.entries(data.digitalfeature || {}).map(
            ([fid, rawMeta]) => {
              const meta = digitalById[String(fid)];
              const label = meta?.name || `Other service #${fid}`;

              const { amount: lineAmt, note } =
                singleDigitalFeatureAmount(rawMeta, data);

              return (
                <tr key={fid}>
                  <td />
                  <td>{label}</td>

                  <td className="calcCell">
                    {note || `Rs. ${fmtRs(lineAmt)}`}
                  </td>

                  <td className="amountCol">{fmtRs(lineAmt)}</td>
                </tr>
              );
            }
          )}

          <tr className="billingSubtotal">
            <td />
            <td colSpan={2}>
              <b>Subtotal (other services)</b>
            </td>

            <td className="amountCol">
              <b>{fmtRs(digitalTotal)}</b>
            </td>
          </tr>
        </>
      )}

      <tr className="billingSection">
        <td colSpan={4}>Combined total </td>
      </tr>

      <tr>
        <td />

        <td colSpan={2} className="calcCell">
          <div>Rs. {fmtRs(erpTotal)} — ERP </div>

          {hasServices && (
            <div>
              + Rs. {fmtRs(servicesTotal)} — add-on services
            </div>
          )}

          {Number(data.perId || 0) > 0 && (
            <div>
              + Rs. {fmtRs(idCardTotal)} — ID cards
            </div>
          )}

          {hasDigital && (
            <div>
              + Rs. {fmtRs(digitalTotal)} — other services
            </div>
          )}
        </td>

        <td className="amountCol">
          <b>{fmtRs(grandTotal)}</b>
        </td>
      </tr>
    </tbody>
  </table>

  {data.paymentTerms.length > 0 && (
    <p style={{ marginTop: 12 }}>
      <b>Instalment amounts:</b> see the schedule under{" "}
      <i>Payment Terms</i> (totals to the combined amount above).
    </p>
  )}
  {data.planNotes && data.planNotes.length > 0 && (
    <div style={{ marginTop: 12 }}>
      <b>Note: </b>
      {data.planNotes.map((note, idx) => (
        <p key={idx} style={{ marginTop: 4 }}><b>➢</b>&nbsp;&nbsp;{note}</p>
      ))}
    </div>
  )}
</div>

        {hasDigital && (
          <div className="pdfText" style={{ marginTop: 24 }}>
            <p style={{ fontWeight: "bold" }}>Other services</p>
            <table className="pdfTable">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Price</th>
                  <th>Renewal</th>
                </tr>
              </thead>
              <tbody>
                {digitalRows.map((row) => {
                  const meta = digitalById[row.id];
                  const label = meta?.name || `Other Service #${row.id}`;
                  return (
                    <tr key={row.id}>
                      <td>{label}</td>
                      <td>{row.price !== "" && row.price != null ? `Rs. ${row.price}` : "-"}</td>
                      <td>{row.renewal !== "" && row.renewal != null ? `Rs. ${row.renewal}` : "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {digitalTermsBlocks.length > 0 && (
          <div className="pdfText" style={{ marginTop: 24, textAlign: "left" }}>
            <p style={{ fontWeight: "bold" }}>Terms and conditions (other services)</p>
            {digitalTermsBlocks.map((block) => (
              <div key={block.name} style={{ marginBottom: 12 }}>
                <p style={{ fontWeight: 600 }}>{block.name}</p>
                <ol style={{ marginTop: 4 }}>
                  {block.terms.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        )}

        <h4 style={{ marginTop: 60 }}>In Witness whereof</h4>
        <p style={{ marginTop: 10, lineHeight: 2 }}>
          This Order Form is executed by persons duly authorised.
        </p>

        <table className="signatureTable">
          <thead>
            <tr>
              <th>Edubricz Technologies LLP</th>
              <th>{data.schoolName}</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ height: 80 }}>
              <td style={{ borderBottom: "none" }}>Signature</td>
              <td style={{ borderBottom: "none" }}>Signature</td>
            </tr>
            <tr style={{ height: 40 }}>
              <td style={{ borderTop: "none" }}></td>
              <td style={{ borderTop: "none" }}></td>
            </tr>
            <tr>
              <td>Name : {data.salesPersonName}</td>
              <td>Name : {data.contactPerson}</td>
            </tr>
            <tr>
              <td>Title : {data.salesPersonTitle}</td>
              <td>Title : {data.contactPersonTitle}</td>
            </tr>
            <tr>
              <td>Email : {data.salesPersonEmail}</td>
              <td>Email : {data.contactPersonEmail}</td>
            </tr>
            <tr>
              <td>Date : {fmtDate(data.agreementdate)}</td>
              <td>Date : {fmtDate(data.agreementdate)}</td>
            </tr>
            <tr>
              <td>Phone no : {data.salesPersonPhoneno}</td>
              <td>Phone no : {data.contactPersonPhoneno}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="pdfButtonRow">
        <button onClick={() => history("/agreement/list")} className="pdfButton">
          Back to List
        </button>
        <button type="button" onClick={generatePrint} className="pdfButton">
          Print / Save as PDF
        </button>
      </div>
    </div>
  );
};

export default PreviewPage;


